// --------------------------------------------------------------
// Created On: 2024-04-15
// Author: KC Willard
//
// Last Modified: 2025-11-06
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useState } from "react";
import Modal from "../Modal/Modal";
import styles from "./ColorPicker.module.scss";

// Interactive color picker. Allows a selection of one color from an array of colors.
export default function ColorPicker(props: Props): Component {
  const [showModal, setShowModal] = useState<boolean>(false);
  const paintColorIndex = props.colors.findIndex((color) => color.paintColorId === props.selectedColorId);
  // Select a color.
  function selectColor(color: number | null): void {
    setShowModal(false);
    props.onChange(color);
  }

  return (
    <Fragment>
      <div data-test="color-picker" className={styles.wrapper}>
        {props.selectedColorId ? (
          <div
            className={`${styles.selectableColor} ${styles.selected}`}
            style={{ backgroundColor: props.colors[paintColorIndex].hexCode }}
            onClick={() => {
              props.disabled ? setShowModal(false) : setShowModal(true);
            }}
          />
        ) : (
          <div
            className={`${styles.selectableColor} ${styles.selected}`}
            style={{ backgroundColor: "var(--bs-danger)" }}
            onClick={() => {
              props.disabled ? setShowModal(false) : setShowModal(true);
            }}
          >
            <i className={`${styles.noPaintColorSwatch} fa fa-fw fa-times`} />
          </div>
        )}
      </div>
      <Modal
        backdropClassName={`${styles.modal} ${styles.backdrop}`}
        style={{ zIndex: "var(--super-modal-z-index)" }}
        show={showModal}
        onHide={() => setShowModal(false)}
        size="sm"
        centered
      >
        <div className="m-2">
          <ul className={styles.colorPickerList} data-test="color-picker-list">
            <li className="border-bottom">
              <div className="row align-items-center">
                <div
                  data-test="color-picker-list-item"
                  className={`col-1 ${styles.selectableColor} ${styles.option}`}
                  onClick={() => selectColor(null)}
                >
                  <i className={`${styles.noPaintColorSquare} fa fa-fw fa-times-circle`} />
                </div>
                <div className="col">No Paint</div>
              </div>
            </li>
            {props.colors.map((color) => (
              <li className="border-bottom" key={color.paintColorId}>
                <div className="row align-items-center">
                  <div
                    className={`col-1 ${styles.selectableColor} ${styles.option}`}
                    style={{ backgroundColor: color.hexCode }}
                    onClick={() => selectColor(color.paintColorId)}
                  />
                  <div className="col">{color.name}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </Modal>
    </Fragment>
  );
}

interface Props {
  colors: PaintColor[];
  selectedColorId: number | null;
  disabled?: boolean;
  onChange: (selectedColorId: number | null) => void;
}

interface PaintColor {
  paintColorId: number;
  hexCode: string;
  name: string;
}
